import { Grid, Typography } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import type { Dispatch, SetStateAction } from 'react';
import { maxTime } from 'utils/roundingRules';

type TranslationFn = (key: string) => string;
type ErrorsMap = Record<string, string | undefined>;

type UnplannedFormData = {
    UnplannedEntryRounding: Dayjs;
    UnplannedEntryGracePeriod: Dayjs;
    UnplannedExitRounding: Dayjs;
    UnplannedExitGracePeriod: Dayjs;
    UnplannedTransferRounding: Dayjs;
    UnplannedTransferGracePeriod: Dayjs;
};

interface UnplannedProps {
    t: TranslationFn;
    loadingGettingRounding?: boolean;
    formData: UnplannedFormData;
    updateFormData: (field: keyof UnplannedFormData, value: Dayjs) => void;
    errors: ErrorsMap;
    setErrors: Dispatch<SetStateAction<ErrorsMap>>;
}

const Unplanned = ({ t, loadingGettingRounding = false, formData, updateFormData, errors, setErrors }: UnplannedProps) => {
    return (
        <>
            <Typography variant="body2">
                <b>{t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.punch_rounding')}</b>
            </Typography>

            <br />
            <br />

            <Grid container spacing={2}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Typography variant="body2">
                            <b>{t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.in')}</b>
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.rounding')}
                            value={formData.UnplannedEntryRounding}
                            onChange={(nv) => nv && updateFormData('UnplannedEntryRounding', nv)}
                            ampm={false}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.grace_period')}
                            value={formData.UnplannedEntryGracePeriod}
                            onChange={(nv) => nv && updateFormData('UnplannedEntryGracePeriod', nv)}
                            ampm={false}
                            maxTime={maxTime(formData.UnplannedEntryRounding)}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            onError={(error) => setErrors({ ...errors, UnplannedEntryGracePeriod: error ?? '' })}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    helperText: errors['UnplannedEntryGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                },
                            }}
                            disabled={loadingGettingRounding}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Typography variant="body2">
                            <b>{t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.out')}</b>
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.rounding')}
                            value={formData.UnplannedExitRounding}
                            onChange={(nv) => nv && updateFormData('UnplannedExitRounding', nv)}
                            ampm={false}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.grace_period')}
                            value={formData.UnplannedExitGracePeriod}
                            onChange={(nv) => nv && updateFormData('UnplannedExitGracePeriod', nv)}
                            ampm={false}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            maxTime={maxTime(formData.UnplannedExitRounding)}
                            onError={(error) => setErrors({ ...errors, UnplannedExitGracePeriod: error ?? '' })}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    helperText: errors['UnplannedExitGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                },
                            }}
                            disabled={loadingGettingRounding}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Typography variant="body2">
                            <b>{t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.transfer')}</b>
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.rounding')}
                            value={formData.UnplannedTransferRounding}
                            onChange={(nv) => nv && updateFormData('UnplannedTransferRounding', nv)}
                            ampm={false}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.unplanned_punch.grace_period')}
                            value={formData.UnplannedTransferGracePeriod}
                            onChange={(nv) => nv && updateFormData('UnplannedTransferGracePeriod', nv)}
                            ampm={false}
                            maxTime={maxTime(formData.UnplannedTransferRounding)}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            onError={(error) => setErrors({ ...errors, UnplannedTransferGracePeriod: error ?? '' })}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    helperText: errors['UnplannedTransferGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                },
                            }}
                            disabled={loadingGettingRounding}
                        />
                    </Grid>
                </LocalizationProvider>
            </Grid>
        </>
    );
};

export default Unplanned;
